import { Box, Flex } from '@chakra-ui/react'
import { useApp, useAuth, useMe, UseThreadMessagesNew } from '@hb/react'
import { AppName, MessageThread, ThreadType } from '@hb/shared'
import {
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useIsMainWindow } from '../../../../contexts/MainWindowContext'
import { reportThreadRead } from '../../utils'
import { useScrollToMessage, useThreadScroll } from './hooks'
import { OtherMessagesStatus } from './OtherMessagesStatus'
import { ThreadMessageView } from './ThreadMessageView'

interface ThreadMessageListProps {
  manuallyMarkedUnread: boolean
  data: UseThreadMessagesNew
  thread?: MessageThread | null
  assessmentThreadUnread?: boolean
  threadOpen: boolean
  threadId?: string
}

export const ThreadMessagesList = forwardRef<HTMLDivElement, ThreadMessageListProps>(
  ({ manuallyMarkedUnread, data, thread, threadId, assessmentThreadUnread, threadOpen }, ref) => {
    const { appName } = useApp()
    const { authUser } = useAuth()
    const me = useMe()

    const {
      data: messages,
      selectedMessage,
      loadingBaseGroups: loading,
      hasNextGroup: hasLaterMessages,
      hasPreviousGroup: hasEarlierMessages,
      fetchNextGroup: fetchLaterMessages,
      fetchPreviousGroup: fetchEarlierMessages,
      loadingNextGroup: loadingLaterMessages,
      loadingPreviousGroup: loadingEarlierMessages,
    } = data

    const [unreadMessageId] = useState(
      () => messages.find(m => m.unreadBy?.includes(me?.uid ?? ''))?.createdOn,
    )
    const lastFetchTime = useRef(0)
    const isMainWindow = useIsMainWindow()
    const windowRef = useRef<HTMLDivElement>(null)

    const scrollContainerRef = ref as RefObject<HTMLDivElement>
    const getFirstMessage = useCallback(() => {
      const earliestMessageEl = scrollContainerRef.current?.querySelector('[data-id]')
      const earliestMessageId = earliestMessageEl?.getAttribute('data-id')
      const earliestMessageOffset = (earliestMessageEl as HTMLElement).offsetTop
      return earliestMessageId ? { id: earliestMessageId, offset: earliestMessageOffset } : null
    }, [scrollContainerRef])

    const getLastMessage = useCallback(() => {
      const messageEls = scrollContainerRef.current?.querySelectorAll('[data-id]')
      const lastMessageEl = messageEls[messageEls.length - 1]
      const lastMessageId = lastMessageEl?.getAttribute('data-id')
      const lastMessageOffset = (lastMessageEl as HTMLElement).offsetTop
      return lastMessageId ? { id: lastMessageId, offset: lastMessageOffset } : null
    }, [scrollContainerRef])

    const waitForScrolledMessage = useCallback(
      (
        prevMessage: { id: string; offset: number },
        scrollContainerEl: HTMLDivElement,
        maxScroll: number,
        direction: 'up' | 'down',
      ) => {
        // set up mutation observer on earliest message
        const newObserver = new MutationObserver(() => {
          const updatedMessageEl = scrollContainerRef.current?.querySelector(
            `[data-id="${prevMessage.id}"]`,
          )
          const newMessage = direction === 'up' ? getFirstMessage() : getLastMessage()
          if (updatedMessageEl && newMessage?.id !== prevMessage.id) {
            const newOffset = (updatedMessageEl as HTMLElement).offsetTop
            const delta = newOffset - prevMessage.offset
            if (delta) {
              scrollContainerEl.scrollTop = direction === 'down' ? maxScroll + delta : delta
              if (direction === 'up') {
                fetchingEarlier.current = false
              } else {
                fetchingLater.current = false
              }
              newObserver.disconnect()
            }
          }
        })
        newObserver.observe(scrollContainerEl, {
          attributes: true,
          childList: true,
          subtree: true,
        })
        mutationObserver.current = newObserver
      },
      [scrollContainerRef, getFirstMessage, getLastMessage],
    )

    // Track scroll position for maintenance during infinite loading
    const wasAtBottom = useRef(true)

    // Use the passed ref as our scroll container

    // Handle scroll events and infinite loading
    // const fetchingEarlierFirstId = useRef<{ id: string; offset: number } | null>(null)
    // const fetchingLaterFirstId = useRef<{ id: string; offset: number } | null>(null)
    const fetchingEarlier = useRef(false)
    const fetchingLater = useRef(false)
    const mutationObserver = useRef<MutationObserver | null>(null)
    const handleScroll = useCallback(
      (scrollTop: number, direction: 'up' | 'down') => {
        const windowEl = windowRef.current
        const scrollContainerEl = scrollContainerRef.current
        if (!windowEl || !scrollContainerEl) return
        const maxScroll = Math.max(0, scrollContainerEl.scrollHeight - windowEl.clientHeight)
        // const progress = maxScroll === 0 ? 0 : scrollTop / maxScroll

        // Track if we're at the bottom for scroll position maintenance
        const distanceToBottom = maxScroll - scrollTop
        if (!fetchingLater.current) {
          wasAtBottom.current = distanceToBottom < 10
        } else {
          wasAtBottom.current = false
        }

        // Load earlier messages when scrolled to top
        if (
          scrollTop <= 20 &&
          hasEarlierMessages &&
          direction === 'up' &&
          !fetchingEarlier.current
        ) {
          const now = Date.now()
          if (now - lastFetchTime.current > 250) {
            lastFetchTime.current = now
            const earliestMessage = getFirstMessage()
            if (earliestMessage) {
              fetchingEarlier.current = true
              fetchEarlierMessages()
              // set up mutation observer on earliest message
              waitForScrolledMessage(earliestMessage, scrollContainerEl, maxScroll, 'up')
            }
          }
        }

        if (
          scrollTop > maxScroll - 20 &&
          hasLaterMessages &&
          direction === 'down' &&
          !fetchingLater.current
        ) {
          wasAtBottom.current = false
          const now = Date.now()
          if (now - lastFetchTime.current > 250) {
            lastFetchTime.current = now
            const lastMessage = getLastMessage()
            if (lastMessage) {
              fetchingLater.current = true
              fetchLaterMessages().then(() => {
                fetchingLater.current = false
              })
            }
          }
        }
      },
      [
        hasEarlierMessages,
        hasLaterMessages,
        fetchEarlierMessages,
        waitForScrolledMessage,
        fetchLaterMessages,
        scrollContainerRef,
        getFirstMessage,
        getLastMessage,
      ],
    )

    const { scrollTo } = useThreadScroll(scrollContainerRef, handleScroll)

    const initialized = useRef(loading)

    useEffect(() => {
      if (loading) {
        initialized.current = false
      }
    }, [loading])

    // Auto-scroll to selected message when it changes
    const scrollToMessage = useScrollToMessage(
      selectedMessage,
      loading,
      scrollContainerRef,
      scrollTo,
    )

    // Load initial messages if no scroll is possible
    const initializedLoad = useRef(false)
    useEffect(() => {
      const scrollContainerEl = scrollContainerRef.current
      const windowEl = windowRef.current
      if (
        loading ||
        loadingEarlierMessages ||
        loadingLaterMessages ||
        initializedLoad.current ||
        !windowEl ||
        !scrollContainerEl
      )
        return

      const maxScroll = Math.max(0, scrollContainerEl.scrollHeight - windowEl.clientHeight)
      if (maxScroll === 0) {
        if (hasEarlierMessages) {
          fetchEarlierMessages()
        }
        if (hasLaterMessages) {
          fetchLaterMessages()
        }
        if (selectedMessage && hasEarlierMessages) {
          fetchEarlierMessages()
        }
      } else {
        initializedLoad.current = true
        if (selectedMessage) {
          const selectedMessageEl = scrollContainerEl.querySelector(
            `[data-id="${selectedMessage.messageId}"]`,
          )
          if (selectedMessageEl) {
            scrollContainerEl.scrollTop = (selectedMessageEl as HTMLElement).offsetTop - 20
          }
        } else {
          const maxScroll = Math.max(0, scrollContainerEl.scrollHeight - windowEl.clientHeight)
          scrollContainerEl.scrollTop = maxScroll
        }
      }
    }, [
      fetchEarlierMessages,
      fetchLaterMessages,
      hasEarlierMessages,
      hasLaterMessages,
      selectedMessage,
      loading,
      loadingEarlierMessages,
      loadingLaterMessages,
      scrollContainerRef,
      windowRef,
      scrollToMessage,
    ])

    useLayoutEffect(() => {
      const scrollContainerEl = scrollContainerRef.current
      if (wasAtBottom.current && scrollContainerEl) {
        scrollTo(scrollContainerEl.scrollHeight)
      }
    }, [scrollTo, scrollContainerRef, messages])

    const [shouldReportRead, setShouldReportRead] = useState<{
      appName: AppName
      type: ThreadType
      threadId: string
      thread: MessageThread
    } | null>(null)

    useEffect(() => {
      if (threadOpen) {
        setShouldReportRead(
          !loading &&
            !manuallyMarkedUnread &&
            me &&
            threadId &&
            thread?.type &&
            (thread.unreadBy.includes(me.uid) || assessmentThreadUnread) &&
            isMainWindow
            ? { appName, type: thread.type, threadId, thread }
            : null,
        )
      }
    }, [
      threadOpen,
      loading,
      manuallyMarkedUnread,
      me,
      threadId,
      thread,
      appName,
      assessmentThreadUnread,
      isMainWindow,
    ])

    useEffect(() => {
      const uid = authUser?.uid
      if (shouldReportRead && threadOpen && uid) {
        reportThreadRead(shouldReportRead.appName, shouldReportRead.type, shouldReportRead.threadId)
        setShouldReportRead(null)
      }
    }, [shouldReportRead, authUser, threadOpen, assessmentThreadUnread])

    return (
      <Flex
        bg="#f4f4f4"
        height="100%"
        flexFlow="column"
        width="100%"
        position="relative"
        ref={windowRef}
        userSelect="none">
        <Box ref={scrollContainerRef} height="100%" w="100%" overflowY="auto">
          <OtherMessagesStatus
            hasMore={hasEarlierMessages}
            loadingMore={loadingEarlierMessages}
            range="earlier"
          />
          {messages.map(message => (
            <ThreadMessageView
              withUnreadDivider={message.createdOn === unreadMessageId}
              message={message}
              selected={selectedMessage?.messageId === message.id}
              scrollToMessage={scrollToMessage}
              key={message.id}
            />
          ))}
          <OtherMessagesStatus
            hasMore={hasLaterMessages}
            loadingMore={loadingLaterMessages}
            range="later"
          />
        </Box>
      </Flex>
    )
  },
)
